import React from "react";

import {
    OrderDeliveryTypeDto,
    OrderPaymentTypeDto,
    OrderStatusDto,
} from "services/orders/restModels";
import { dateToInputValue, inputValueToDate } from "shared/lib/dateInput";
import { localizedText } from "shared/lib/localizedMap";
import {
    ORDER_DELIVERY_TYPE_FILTER_OPTIONS,
    ORDER_PAYMENT_TYPE_FILTER_OPTIONS,
    ORDER_STATUS_FILTER_OPTIONS,
    orderDeliveryTypeLabel,
    orderPaymentTypeLabel,
    orderStatusLabel,
} from "shared/lib/orderLabels";
import { Button, DateField, LabeledField, Select } from "shared/ui";

import { useOrdersContext } from "features/Orders/context";
import { OrdersPaidFilter } from "features/Orders/types";

import { FiltersGrid, ResetCell } from "./styles";

// Статус оплаты — единственный фильтр с одним значением: «оплачен» и «не оплачен» друг друга
// исключают, и выбрать оба означало бы не фильтровать вовсе.
const PAID_ANY_VALUE = "";
const PAID_VALUES: { value: string; label: string; paid: OrdersPaidFilter }[] = [
    { value: PAID_ANY_VALUE, label: "Любой", paid: null },
    { value: "paid", label: "Оплачен", paid: true },
    { value: "unpaid", label: "Не оплачен", paid: false },
];

const paidValue = (paid: OrdersPaidFilter) =>
    (PAID_VALUES.find((item) => item.paid === paid) || PAID_VALUES[0]).value;

const paidFromValue = (value: string): OrdersPaidFilter =>
    (PAID_VALUES.find((item) => item.value === value) || PAID_VALUES[0]).paid;

export const OrdersFilters = () => {
    const state = useOrdersContext();

    return (
        <FiltersGrid>
            <LabeledField label="Меню" htmlFor="orders-menu">
                <Select
                    id="orders-menu"
                    title="Меню"
                    value={state.menuId}
                    onChange={(value) => state.setMenuId(Number(value))}
                    options={state.menuOptions.map((menu) => ({
                        value: menu.id,
                        label: localizedText(menu.name, state.locale),
                    }))}
                />
            </LabeledField>

            <LabeledField label="Заказы с" htmlFor="orders-from">
                <DateField
                    id="orders-from"
                    value={dateToInputValue(state.from)}
                    max={dateToInputValue(state.to)}
                    // Недописанную дату поле отдаёт как null — такую не применяем, иначе
                    // период уезжает в нулевой год прямо во время набора.
                    onChange={(value) => {
                        const date = inputValueToDate(value);

                        if (date) state.setFrom(date);
                    }}
                />
            </LabeledField>

            <LabeledField label="по" htmlFor="orders-to">
                <DateField
                    id="orders-to"
                    value={dateToInputValue(state.to)}
                    min={dateToInputValue(state.from)}
                    onChange={(value) => {
                        const date = inputValueToDate(value);

                        if (date) state.setTo(date);
                    }}
                />
            </LabeledField>

            <LabeledField label="Статус заказа" htmlFor="orders-statuses">
                <Select
                    multiple
                    id="orders-statuses"
                    title="Статус заказа"
                    emptyLabel="Любой"
                    value={state.statuses}
                    onChange={(values) => state.setStatuses(values as OrderStatusDto[])}
                    options={ORDER_STATUS_FILTER_OPTIONS.map((status) => ({
                        value: status,
                        label: orderStatusLabel(status),
                    }))}
                />
            </LabeledField>

            <LabeledField label="Способ получения" htmlFor="orders-delivery-types">
                <Select
                    multiple
                    id="orders-delivery-types"
                    title="Способ получения"
                    emptyLabel="Любой"
                    value={state.deliveryTypes}
                    onChange={(values) => state.setDeliveryTypes(values as OrderDeliveryTypeDto[])}
                    options={ORDER_DELIVERY_TYPE_FILTER_OPTIONS.map((type) => ({
                        value: type,
                        label: orderDeliveryTypeLabel(type),
                    }))}
                />
            </LabeledField>

            <LabeledField label="Тип оплаты" htmlFor="orders-payment-types">
                <Select
                    multiple
                    id="orders-payment-types"
                    title="Тип оплаты"
                    emptyLabel="Любой"
                    value={state.paymentTypes}
                    onChange={(values) => state.setPaymentTypes(values as OrderPaymentTypeDto[])}
                    options={ORDER_PAYMENT_TYPE_FILTER_OPTIONS.map((type) => ({
                        value: type,
                        label: orderPaymentTypeLabel(type),
                    }))}
                />
            </LabeledField>

            <LabeledField label="Статус оплаты" htmlFor="orders-paid">
                <Select
                    id="orders-paid"
                    title="Статус оплаты"
                    value={paidValue(state.paid)}
                    onChange={(value) => state.setPaid(paidFromValue(value))}
                    options={PAID_VALUES.map((item) => ({
                        value: item.value,
                        label: item.label,
                    }))}
                />
            </LabeledField>

            {state.isFiltered && (
                <ResetCell>
                    <Button variant="text" size="small" onClick={state.resetFilters}>
                        Сбросить фильтры
                    </Button>
                </ResetCell>
            )}
        </FiltersGrid>
    );
};
